import type { Coordinate } from 'ol/coordinate';
import * as proj from 'ol/proj';
import { computed, ref } from 'vue';

import type { SymbolDTO } from '@nora-monorepo/nora-api-client';
import { SymbolUsageEnum } from '@nora-monorepo/nora-api-client';
import type { NoraCoordinateFormat } from '@nora-monorepo/shared';
import {
  appLogger,
  formatLatLongCoordinate,
  i18n,
  LatLong,
  useApiStore,
  useGenericUserStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import { simulatePosition } from '#nora-ui/global/simulate-position';
import { MapContextMenuAction } from '#nora-ui/map/components/controls/context-menu/map-context-menu.types';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';
import { useSymbolStore } from '#nora-ui/stores/symbol-store';

interface MapContextMenuCallbacks {
  onClose: () => void;
  onOpen: (position: { x: number; y: number }) => void;
  onPositionChanged?: (position: { x: number; y: number }) => void;
}

export class MapContextMenuManager {
  private readonly t = i18n.global.t;
  private readonly api = useApiStore();
  private readonly symbolStore = useSymbolStore();
  private readonly toastStore = useToastStore();
  private readonly userStore = useGenericUserStore();

  private callbacks?: MapContextMenuCallbacks;
  private favoriteSymbolsRequestId = 0;

  readonly clickedCoordinate = ref<Coordinate | null>(null);
  readonly isOpen = ref(false);
  readonly clickedLocation = ref<LatLong | null>(null);
  readonly favoriteSymbols = ref<SymbolDTO[]>([]);
  readonly isLoadingFavoriteSymbols = ref(false);

  readonly hasLocation = computed(() =>
    this.userStore.user?.lastTrack !== undefined && this.userStore.user.lastTrack !== null,
  );

  get canPlaceSymbol() {
    return this.map.drawToolManager.canDraw;
  }

  constructor(private readonly map: NoraMap) {}

  showMenu(event: MouseEvent): void {
    event.preventDefault();

    if (!this.callbacks) {
      return;
    }

    const olMap = this.map.getOlMapOrThrow();
    const pixel = olMap.getEventPixel(event);
    const coordinate = olMap.getCoordinateFromPixel(pixel);
    const [longitude, latitude] = proj.toLonLat(coordinate, this.map.ref.projection);

    if (latitude === undefined || longitude === undefined) {
      return;
    }

    this.clickedCoordinate.value = coordinate;
    this.clickedLocation.value = new LatLong(latitude, longitude);

    this.isOpen.value = true;
    this.callbacks.onOpen({ x: event.clientX, y: event.clientY });
  }

  /** Keeps the action box attached to the original location during automatic map updates. */
  private readonly updatePosition = (): void => {
    if (!this.isOpen.value || !this.clickedCoordinate.value) {
      return;
    }

    const olMap = this.map.getOlMapOrThrow();
    const viewport = olMap.getViewport().getBoundingClientRect();
    const [pixelX, pixelY] = olMap.getPixelFromCoordinate(this.clickedCoordinate.value);

    const isOutsideViewport
      = pixelX < 0
        || pixelX > viewport.width
        || pixelY < 0
        || pixelY > viewport.height;

    if (isOutsideViewport) {
      this.hideMenu();
      return;
    }

    this.callbacks?.onPositionChanged?.({
      x: viewport.left + pixelX,
      y: viewport.top + pixelY,
    });
  };

  readonly hideMenu = (): void => {
    this.isOpen.value = false;
    this.callbacks?.onClose();
  };

  async loadFavoriteSymbols(): Promise<void> {
    const requestId = ++this.favoriteSymbolsRequestId;
    this.isLoadingFavoriteSymbols.value = true;

    try {
      const symbols = await this.symbolStore.getFavouriteSymbols(SymbolUsageEnum.MARKER);

      if (requestId === this.favoriteSymbolsRequestId) {
        this.favoriteSymbols.value = symbols;
      }
    }
    catch (error) {
      if (requestId !== this.favoriteSymbolsRequestId) {
        return;
      }

      appLogger.error({
        message: 'Could not load quick access symbols',
        sensitiveContext: { error },
      });
      this.toastStore.addError({
        message: this.t('mapContextMenu.errors.loadQuickAccessSymbols'),
      });
    }
    finally {
      if (requestId === this.favoriteSymbolsRequestId) {
        this.isLoadingFavoriteSymbols.value = false;
      }
    }
  }

  readonly handleActionSelected = (actionId: string | null): void => {
    if (actionId === MapContextMenuAction.PlaceSymbol) {
      void this.loadFavoriteSymbols();
    }
  };

  readonly placeSymbol = async (symbol: SymbolDTO): Promise<void> => {
    if (!this.clickedCoordinate.value) {
      return;
    }

    try {
      await this.map.drawToolUI.placeSymbolAt(this.clickedCoordinate.value, {
        symbolId: symbol.id,
        symbolName: symbol.name,
        symbolPackId: symbol.symbolPackId,
      });
      this.toastStore.addSuccess({
        message: this.t('mapContextMenu.symbolPlaced', { symbolName: symbol.name }),
      });
      this.hideMenu();
    }
    catch (error) {
      appLogger.error({
        message: 'Could not place symbol',
        sensitiveContext: {
          error,
          symbolId: symbol.id,
          symbolPackId: symbol.symbolPackId,
        },
      });
      this.toastStore.addError({ message: this.t('mapContextMenu.errors.placeSymbol') });
    }
  };

  readonly setLocation = async (): Promise<void> => {
    if (!this.clickedLocation.value) {
      return;
    }

    try {
      await simulatePosition({
        latitude: this.clickedLocation.value.latitude,
        longitude: this.clickedLocation.value.longitude,
      });
      this.toastStore.addSuccess({ message: this.t('mapContextMenu.positionSet') });
      this.hideMenu();
    }
    catch (error) {
      appLogger.error({
        message: 'Could not set position',
        sensitiveContext: { error },
      });
      this.toastStore.addError({ message: this.t('mapContextMenu.errors.setPosition') });
    }
  };

  readonly clearLocation = async (): Promise<void> => {
    try {
      await this.api.unwrap(this.api.nexusUser.clearLocation());
      const userId = this.userStore.user?.id;

      if (userId !== undefined) {
        this.userStore.clearLastTrack(userId);
        useOperationStore().clearUserLastTrack(userId);

        for (const map of useMapStore().maps) {
          map.state.track.removeTrack(userId);
        }
      }

      this.toastStore.addSuccess({ message: this.t('mapContextMenu.positionCleared') });
      this.hideMenu();
    }
    catch (error) {
      appLogger.error({
        message: 'Could not clear position',
        sensitiveContext: { error },
      });
      this.toastStore.addError({ message: this.t('mapContextMenu.errors.clearPosition') });
    }
  };

  readonly openInGoogleMaps = (): void => {
    if (!this.clickedLocation.value) {
      return;
    }

    window.open(
      `https://www.google.com/maps?q=${this.clickedLocation.value.latitude},${this.clickedLocation.value.longitude}`,
      '_blank',
    );
    this.hideMenu();
  };

  readonly copyPosition = async (format: NoraCoordinateFormat, labelKey: string): Promise<void> => {
    if (!this.clickedLocation.value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formatLatLongCoordinate(this.clickedLocation.value, format));
      this.toastStore.addSuccess({
        message: this.t('mapContextMenu.positionCopied', {
          format: this.t(labelKey),
        }),
      });
      this.hideMenu();
    }
    catch (error) {
      appLogger.error({
        message: 'Could not copy position',
        sensitiveContext: { error, format },
      });
      this.toastStore.addError({ message: this.t('mapContextMenu.errors.copyPosition') });
    }
  };

  attach(callbacks: MapContextMenuCallbacks): void {
    this.detach();
    const olMap = this.map.getOlMapOrThrow();

    this.callbacks = callbacks;
    olMap.on('pointerdrag', this.hideMenu);
    olMap.on('postrender', this.updatePosition);
    olMap.getViewport().addEventListener('wheel', this.hideMenu);
    document.addEventListener('click', this.hideMenu);
    document.addEventListener('contextmenu', this.hideMenu, true);
  }

  detach(): void {
    this.isOpen.value = false;
    this.favoriteSymbolsRequestId++;
    this.isLoadingFavoriteSymbols.value = false;

    if (!this.callbacks) {
      return;
    }

    const olMap = this.map.getOlMapOrThrow();

    olMap.un('pointerdrag', this.hideMenu);
    olMap.un('postrender', this.updatePosition);
    olMap.getViewport().removeEventListener('wheel', this.hideMenu);
    document.removeEventListener('click', this.hideMenu);
    document.removeEventListener('contextmenu', this.hideMenu, true);
    this.callbacks = undefined;
  }
}
